import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  activeProfile, clearProfileData as clearProfileDataInDocument, createApplicationDocument,
  createProfile as createProfileInDocument, deleteProfile as deleteProfileInDocument,
  importProfile as importProfileInDocument,
  replaceProfileData as replaceProfileDataInDocument,
  renameProfile as renameProfileInDocument, switchProfile as switchProfileInDocument,
  updateActiveProfileData
} from "../domain/applicationDocument.js";
import { createDocumentRepository } from "./documentRepository.js";
import { scheduleAutomaticBackup } from "./backupService.js";
import { clearOnboardingProgress, isPristineProfile, markOnboardingStarted } from "./onboardingPreferences.js";
import { clearProfilePresentationState } from "./presentationState.js";
import { forgetSharedProfile } from "./profileSharingPreferences.js";

let sharedRepository;
const ApplicationDocumentContext = createContext(null);

function defaultRepository() {
  sharedRepository ??= createDocumentRepository();
  return sharedRepository;
}

export function useApplicationDocument(repositoryOverride) {
  const sharedApplication = useContext(ApplicationDocumentContext);
  const standaloneApplication = useStandaloneDocument(repositoryOverride, !sharedApplication);
  return sharedApplication ?? standaloneApplication;
}

export function ApplicationDocumentProvider({ children, repository: repositoryOverride }) {
  const repository = useMemo(() => repositoryOverride ?? defaultRepository(), [repositoryOverride]);
  const [applicationDocument, setApplicationDocument] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    repository.loadDocument()
      .then((loadedDocument) => { if (active) setApplicationDocument(loadedDocument); })
      .catch((loadError) => { if (active) setError(loadError); });
    return () => { active = false; };
  }, [repository]);

  const commit = useCallback(async (nextApplicationDocument) => {
    setSaving(true);
    setError(null);
    try {
      await repository.saveDocument(nextApplicationDocument);
      setApplicationDocument(nextApplicationDocument);
      scheduleAutomaticBackup(nextApplicationDocument);
      return true;
    } catch (saveError) {
      setError(saveError);
      return false;
    } finally {
      setSaving(false);
    }
  }, [repository]);

  const save = useCallback(async (nextDocument) => {
    if (!isPristineProfile(nextDocument)) markOnboardingStarted(applicationDocument.activeProfileId);
    return commit(updateActiveProfileData(applicationDocument, nextDocument));
  }, [applicationDocument, commit]);

  const switchProfile = useCallback(async (profileId) => (
    commit(switchProfileInDocument(applicationDocument, profileId))
  ), [applicationDocument, commit]);

  const createProfile = useCallback(async (draft) => {
    try {
      return commit(createProfileInDocument(applicationDocument, draft));
    } catch (profileError) {
      setError(profileError);
      return false;
    }
  }, [applicationDocument, commit]);

  const renameProfile = useCallback(async (profileId, name) => {
    try {
      return commit(renameProfileInDocument(applicationDocument, profileId, name));
    } catch (profileError) {
      setError(profileError);
      return false;
    }
  }, [applicationDocument, commit]);

  const deleteProfile = useCallback(async (profileId) => {
    try {
      const deleted = await commit(deleteProfileInDocument(applicationDocument, profileId));
      if (deleted) {
        clearProfilePresentationState([profileId]);
        forgetSharedProfile(profileId);
      }
      return deleted;
    } catch (profileError) {
      setError(profileError);
      return false;
    }
  }, [applicationDocument, commit]);

  const importProfile = useCallback(async (draft) => {
    try {
      return commit(importProfileInDocument(applicationDocument, draft));
    } catch (profileError) {
      setError(profileError);
      return false;
    }
  }, [applicationDocument, commit]);

  const replaceProfileData = useCallback(async (profileId, data, now) => {
    try {
      const replaced = await commit(replaceProfileDataInDocument(applicationDocument, profileId, data, now));
      if (replaced) clearProfilePresentationState([profileId]);
      return replaced;
    } catch (profileError) {
      setError(profileError);
      return false;
    }
  }, [applicationDocument, commit]);

  const clearProfileData = useCallback(async (profileId) => {
    const profile = applicationDocument.profiles.find((item) => item.id === profileId);
    if (profile && !isPristineProfile(profile.data)) markOnboardingStarted(profileId);
    return commit(clearProfileDataInDocument(applicationDocument, profileId));
  }, [applicationDocument, commit]);

  const restoreApplicationDocument = useCallback((nextApplicationDocument) => commit(nextApplicationDocument), [commit]);

  const resetApplicationDocument = useCallback(async () => {
    const profileIds = applicationDocument.profiles.map((profile) => profile.id);
    const reset = await commit(createApplicationDocument());
    if (reset) {
      clearOnboardingProgress(profileIds);
      clearProfilePresentationState(profileIds);
      for (const profileId of profileIds) forgetSharedProfile(profileId);
    }
    return reset;
  }, [applicationDocument, commit]);

  const value = useMemo(() => {
    const profile = applicationDocument ? activeProfile(applicationDocument) : null;
    return {
      applicationDocument,
      document: profile?.data ?? null,
      profile,
      profiles: applicationDocument?.profiles ?? [],
      error,
      saving,
      save,
      switchProfile,
      createProfile,
      renameProfile,
      deleteProfile,
      importProfile,
      replaceProfileData,
      clearProfileData,
      restoreApplicationDocument,
      resetApplicationDocument
    };
  }, [applicationDocument, clearProfileData, createProfile, deleteProfile, error, importProfile, renameProfile, replaceProfileData, resetApplicationDocument, restoreApplicationDocument, save, saving, switchProfile]);

  return createElement(ApplicationDocumentContext.Provider, { value }, children);
}

function useStandaloneDocument(repositoryOverride, enabled) {
  const repository = useMemo(
    () => enabled ? (repositoryOverride ?? defaultRepository()) : null,
    [enabled, repositoryOverride]
  );
  const [document, setDocument] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!repository) return undefined;
    let active = true;
    repository.loadDocument()
      .then((loadedDocument) => { if (active) setDocument(loadedDocument); })
      .catch((loadError) => { if (active) setError(loadError); });
    return () => { active = false; };
  }, [repository]);

  const save = useCallback(async (nextDocument) => {
    if (!repository) return false;
    setSaving(true);
    setError(null);
    try {
      await repository.saveDocument(nextDocument);
      setDocument(nextDocument);
      return true;
    } catch (saveError) {
      setError(saveError);
      return false;
    } finally {
      setSaving(false);
    }
  }, [repository]);

  return { document, error, save, saving };
}
